import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { firstValueFrom, from, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { mitZeitgrenze, ZEITGRENZE } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from 'src/app/data-model';

import { DataService } from 'src/app/data.service';
import { ProjectService } from 'src/app/project.service';

/**
 * Race-Fix (project_race_resolver_active_project, vertagt 2026-06-03):
 *
 * Bei Direct-URL-Reload (`/bohrpunkte/<id>`) lief der Resolver bisher parallel
 * zum Storage-Recovery von `ProjectService.loadProject(...)`. Wenn der Resolver
 * den Punkt schneller lieferte, rendete die Plausi-Component mit
 * `ps.project() === ''` → `isPointAssignedToActiveProject` = false → die
 * Workflow-Buttons wurden weggeworfen, obwohl die Permission-Logik sie
 * erlaubt haette.
 *
 * Loesung: BEVOR der Resolver das Punkt-Doc emittiert, sicherstellen, dass
 * `ProjectService.project()` zur Projekt-Zuordnung des Punkts passt. Wir
 * laden das Projekt synchron NUR dann, wenn `ps.project()` noch leer ist
 * (= Race-Window) — wenn der User ein anderes Projekt aktiv hat, NICHT
 * ungefragt umschalten (Plausi zeigt dann den frozen-Banner; gewolltes
 * Cross-Projekt-Verhalten).
 *
 * Bug 2 „stilles Projekt" (HANDOFF, Produktentscheidung 2026-06-11): Der Load
 * erfolgt TRANSIENT — nur in-memory fuer die Detail-Ansicht, `currentProject`
 * wird nicht persistiert. Die BohrpunktComponent zeigt dazu einen Hinweis-
 * Banner und entlaedt den Kontext beim Verlassen (`unloadTransientProject`).
 */
export async function resolveBohrpunktWithProjectLoad(ds: any, ps: any, id: string): Promise<any> {
	const bp: any = await firstValueFrom(ds.getDocument$(id));
	const pointProjectId: string | undefined =
		bp?.header?.projects?.[0]?.id ?? bp?.workflow?.projectId;
	if (pointProjectId && !ps.project()) {
		// loadProject darf den Resolver nicht blockieren — wenn der User
		// keinen Projekt-Zugriff hat (z.B. Admin ohne Member-Status) oder
		// die DB-Lookup haengt (Replikation nicht fertig), wird die Detail-
		// View trotzdem geoeffnet. Frozen-Banner uebernimmt dann die Sicht.
		// Race-Fix-Wirkung bleibt fuer alle anderen User mit zugaenglichem
		// Projekt bestehen (≤1s typisch). Timeout sichert gegen Hängenbleiben.
		const timeout = new Promise(resolve => setTimeout(resolve, 1500));
		await Promise.race([
			ps.loadProject(pointProjectId, { transient: true }).catch(() => { /* tolerant */ }),
			timeout
		]);
	}
	return ds.updateBohrpunkt(bp);
}

/** Der Punkt liess sich nicht laden, obwohl die Daten bereitstanden. */
export const PUNKT_NICHT_LADBAR =
	'Der Punkt lässt sich gerade nicht öffnen. Er liegt auf diesem Gerät nicht vor, oder der Server hat die Anfrage abgelehnt.';

/** Der Punkt liess sich nicht laden, und die Datenverbindung stand auch nicht. */
export const PUNKT_OHNE_VERBINDUNG =
	'Der Punkt lässt sich gerade nicht öffnen: Die Verbindung zu den Daten steht noch nicht. Bitte gleich noch einmal versuchen.';

/**
 * Oeffnet einen Punkt fuer die Detail-Ansicht — mit Zeitgrenze und Klartext.
 *
 * Nutzer-Befund 19.09.2026: „Protokoll wechseln aus der Seitenleiste heraus geht
 * gerade nicht", und eine Weile spaeter ging es von selbst wieder. Der Klick
 * selbst war in Ordnung; haengen blieb der Schritt davor. Gewartet wurde OHNE
 * ZEITGRENZE darauf, dass der Datendienst „bereit" meldet. Dieses Kennzeichen
 * wird bei jedem Umbau der Datenbanken zurueckgesetzt (Projektwechsel, frische
 * Anmeldung, Offline-Wechsel, Wechsel der Server-Datenbank) und geht erst wieder
 * an, wenn LOKALE **und** SERVER-Datenbank stehen. Fehlt im Online-Modus die
 * Verbindung, kommt die Meldung gar nicht, und jeder weitere Klick lief in
 * dasselbe Warten — ohne Hinweis, ohne Ausweg.
 *
 * Zwei Dinge sind deshalb anders:
 *
 * 1. **Gewartet wird hoechstens `geduldMs`.** Danach wird trotzdem geladen: Zum
 *    LESEN eines Punktes reicht die lokale Datenbank; die Bereit-Meldung haengt
 *    an der Server-Verbindung, die dafuer gar nicht noetig ist.
 * 2. **Ein Fehlschlag wird gesagt.** Scheitert das Laden, bleibt es dabei, dass
 *    der Router die Navigation abbricht und das bisherige Protokoll stehen
 *    bleibt — aber der Grund steht jetzt auf dem Bildschirm. Auf abgebrochene
 *    Navigationen hoert in bpweb sonst niemand.
 */
export async function oeffnePunkt(
	ds: any,
	ps: any,
	id: string,
	melde?: (text: string) => void,
	geduldMs: number = ZEITGRENZE.dienstBereit
): Promise<any> {
	let bereit = true;
	try {
		await mitZeitgrenze(ds.waitForInitialization(), geduldMs, 'Datendienst bereit');
	} catch {
		// Kein Abbruch — der Ladeversuch kommt trotzdem (siehe oben, Punkt 1).
		// Gemerkt wird es nur fuer den Fall, dass er scheitert: Dann ist die
		// fehlende Verbindung die bessere Auskunft als „liegt nicht vor".
		bereit = false;
	}
	try {
		return await resolveBohrpunktWithProjectLoad(ds, ps, id);
	} catch (err) {
		melde?.(bereit ? PUNKT_NICHT_LADBAR : PUNKT_OHNE_VERBINDUNG);
		throw err;
	}
}

export const bohrpunktDetailResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
	const ds = inject(DataService);
	const ps = inject(ProjectService);
	const msg = inject(ToastrService);

	const id = route.paramMap.get('id')!;
	return from(oeffnePunkt(ds, ps, id, text => msg.error(text, 'Protokoll')));
};

export const bohrpunkteDetailResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
	// Sofort leeres Array zurückgeben - die Komponenten laden die Punkte selbst
	// Das verhindert, dass die Navigation blockiert wird
	return of([]);
};

export function updateAccordingly(bp: any, bps: AufnahmePunktDocument[]) {
	return new Array(...bps.map((val: any, index: number, that: AufnahmePunktDocument[]) => {
		if (val._id === bp._id)
			return bp;
		else
			return val;
	}));
}
